import './DiagnosticsPage.css';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useInRouterContext } from 'react-router-dom';
import { BarChart3, ChevronDown, Download, Loader2, Pause, Play, RotateCcw, Search, Settings, Upload, X } from 'lucide-react';
import CleanSelect from './components/CleanSelect';
import { requestDiagnostic } from './data/diagnosticsResource';
import { formatCount, formatDate } from './directory/directoryModel';
import { ALL_DIAGNOSTICS, DIAGNOSTIC_IDS, diagnosticLabel } from '../Diagnostic/diagnosticAvailability';

/*
 * DiagnosticMonitor: the live proctoring view + controls, shared by the admin
 * Diagnostics page (school scope, with Test settings) and the teacher
 * Diagnostic page (class scope, no Test settings) since 2026-09-16 (Sandy:
 * "change the diagnostic page on teacher dashboard to be the same as the
 * admin one with all the same controls except the test settings").
 *
 * The page that mounts it owns the DATA (`feed`): where the students come
 * from (school roster vs class roster) and how the live monitor is polled.
 * This component owns everything the proctor sees and clicks: the sheets,
 * flags, pause / move / set-time, detail modal and the toast; pause-all and
 * Test settings are school-wide and only render for scope='school'.
 *
 * Every action button shows its own spinner while its request runs (Sandy
 * 2026-09-16): `busyKey` names the one in flight, e.g.
 * "individual_pause:<studentId>", and every other control is disabled.
 *
 * Flag display (Sandy): no "Why" column, a flagged student carries one
 * orange (pace) or red (integrity) dot by their name, reasoning on hover.
 */

const CARD = 'bg-white border border-neutral-200 rounded-[6px]';

// Admin pause as the admin sees it (Sandy 2026-09-16): the lambda only
// writes PauseSource='admin' when the STUDENT's next heartbeat applies the
// pending action, up to ~40s after the click. The monitor already returns
// pendingAction='pause' right away, so the row flips to Unpause on that and
// the label says "Pausing" until the student's tab has actually stopped.
export const adminPausedOf = (s) => s.pauseSource === 'admin' || s.pendingAction === 'pause';
const adminPausePending = (s) => s.pendingAction === 'pause' && s.pauseSource !== 'admin';

const MODULES = [
  { key: 'English-1', title: 'English · Module 1', progressKeys: ['English-1'] },
  { key: 'English-2', title: 'English · Module 2', progressKeys: ['English-2e', 'English-2h'] },
  { key: 'Math-1', title: 'Math · Module 1', progressKeys: ['Math-1'] },
  { key: 'Math-2', title: 'Math · Module 2', progressKeys: ['Math-2e', 'Math-2h'] },
];

// Where an admin can move a student (same list the legacy controls offer;
// the lambda resolves Module 2 to the student's adaptive path).
const SECTIONS = [
  { field: 'English', number: '1', label: 'English Module 1' },
  { field: 'English', number: '2', label: 'English Module 2' },
  { field: 'Break', number: '1', label: 'Break' },
  { field: 'Math', number: '1', label: 'Math Module 1' },
  { field: 'Math', number: '2', label: 'Math Module 2' },
];

// Severity buckets (Sandy 2026-08-17): red = integrity (copying, tabbing
// away), orange = pace (racing, behind). A multi-flag student shows their
// worst color; the hover card lists every reason.
const FLAG_META = {
  copying: { label: 'Copy/paste', severity: 'red' },
  tabbing: { label: 'Tabbing away', severity: 'red' },
  clicking: { label: 'Clicking through', severity: 'orange' },
};
const FLAG_ORDER = ['copying', 'tabbing', 'clicking'];
const RED = '#dc2626';
const ORANGE = '#f59e0b';

const copyCount = (b) => Math.max(b?.copyAttempts || 0, b?.progressCopyFlags || 0) + (b?.pastes || 0);

export function formatTime(seconds) {
  if (seconds === null || seconds === undefined) return '--:--';
  const abs = Math.abs(Math.floor(seconds));
  return `${seconds < 0 ? '-' : ''}${Math.floor(abs / 60)}:${(abs % 60).toString().padStart(2, '0')}`;
}

// "Ns / m / h ago" only inside a day (2026-09-25): an administrator read
// "288d ago" as "no way to know when this happened". A day or more shows the
// date itself, e.g. "Sep 12, 2025".
export function formatAgo(isoString, now = Date.now()) {
  if (!isoString) return '';
  const at = new Date(isoString).getTime();
  if (!Number.isFinite(at)) return '';
  const diff = (now - at) / 1000;
  if (diff < 60) return `${Math.max(0, Math.floor(diff))}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return formatDate(isoString, { withYear: true }) || '';
}

// The only text on the page that changes with the clock. It owns its 5-second
// timer (2026-09-25): the whole monitor used to re-render every row every 5
// seconds to keep this one label fresh.
function Ago({ iso }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 5000);
    return () => clearInterval(t);
  }, []);
  return formatAgo(iso);
}

// Admin (school scope) sheets render 100 rows at a time (2026-09-25): a
// 2,052-student school built ~24,000 DOM nodes. Search and grade filters work
// on the full list first. The teacher class view is never capped.
export const SHEET_LIMIT = 100;
const notStartedOf = (s) => !s.started && !s.timerStatus;

// The button sits on the LEFT, next to the count (2026-10-07): on the right
// the fixed Ask Buffy launcher (.admin-buffy, bottom-right) covered it.
function MoreRows({ shown, total, step, onMore }) {
  return (
    <div className="mt-2 flex items-center gap-3 flex-wrap text-[12px] text-neutral-500">
      <button type="button" onClick={onMore} className={GHOST_BTN + ' px-3 py-1.5 text-[12px]'}>
        Show {formatCount(Math.min(step, total - shown))} more
      </button>
      <span className="tabular-nums">Showing {formatCount(shown)} of {formatCount(total)}. Use Search name to find one student.</span>
    </div>
  );
}

// A sheet's title doubles as its collapse toggle (2026-10-07). The sheet
// below it is hidden, not unmounted, so its "Show more" paging and sort
// survive a collapse.
function SectionToggle({ open, onToggle, title, count, small = false, className = '' }) {
  return (
    <button
      type="button"
      aria-expanded={open}
      onClick={onToggle}
      className={'inline-flex items-center gap-1.5 bg-transparent border-0 p-0 cursor-pointer text-left leading-[1.4] group ' + className}
    >
      <ChevronDown
        size={small ? 13 : 15}
        strokeWidth={2.5}
        aria-hidden="true"
        className={'flex-none text-neutral-400 group-hover:text-ink transition-transform ' + (open ? '' : '-rotate-90')}
      />
      <span className={(small ? 'text-[13px]' : 'text-[15px]') + ' font-extrabold text-ink'}>{title}</span>
      {count != null && <span className="ml-1 text-[12px] font-semibold text-neutral-400 tabular-nums">{formatCount(count)}</span>}
    </button>
  );
}

const lastNameKey = (name) => {
  const n = (name || '').trim();
  return (n.split(/\s+/).slice(-1)[0] + ' ' + n).toLowerCase();
};

function boxKeyOf(section) {
  if (!section?.field) return null;
  if (section.field === 'Break') return 'break';
  const n = String(section.number || '1');
  return `${section.field}-${n.startsWith('2') ? '2' : '1'}`;
}

function sectionLabel(section) {
  const key = boxKeyOf(section);
  if (!key) return 'Not started';
  if (key === 'break') return 'On break';
  const mod = MODULES.find((m) => m.key === key);
  const path = String(section.number) === '2e' ? ' (Easy)' : String(section.number) === '2h' ? ' (Hard)' : '';
  return (mod ? mod.title.replace(' · ', ' ') : `${section.field} ${section.number}`) + path;
}

function moduleCell(s) {
  if (s.status === 'Done') return 'Finished';
  if (!s.started && !s.timerStatus) return 'Not started';
  const key = boxKeyOf(s.currentSection);
  if (!key) return 'Not started';
  if (key === 'break') return 'Break';
  const [subj, num] = key.split('-');
  const path = String(s.currentSection?.number) === '2e' ? ' · Easy' : String(s.currentSection?.number) === '2h' ? ' · Hard' : '';
  return `${subj} M${num}${path}`;
}

// English now, Math later (2026-09-23): English is scored when its second
// module ends, so an unfinished student can already have an English score.
export function englishOnlyScore(s) {
  if (s.status === 'Done') return null;
  const english = finishedScore(s, 'reading');
  return english != null && finishedScore(s, 'math') == null ? english : null;
}

function moduleStatOf(s, mod) {
  if (!s.modules) return null;
  for (const k of mod.progressKeys) {
    const m = s.modules[k];
    if (m && (m.answered > 0 || m.furthest > 0 || m.timeSpent > 0)) return { ...m, pathKey: k };
  }
  const first = s.modules[mod.progressKeys[0]];
  return first ? { ...first, pathKey: mod.progressKeys[0] } : null;
}

function questionCell(s) {
  if (s.status === 'Done' || (!s.started && !s.timerStatus)) return null;
  const key = boxKeyOf(s.currentSection);
  if (!key || key === 'break') return null;
  const mod = MODULES.find((m) => m.key === key);
  const stat = mod && moduleStatOf(s, mod);
  if (!stat || !stat.total) return null;
  return { furthest: stat.furthest || 0, total: stat.total };
}

// Thresholds recalibrated on 4,941 real sittings (Sandy 2026-08-28): the old
// lines (1 copy, 3 rushed, 4 tabs) flagged 23-43% of all students - noise.
// A flag should mean the top few percent. Copies 4+ (~p90), tabs 25+ (~p90),
// rushed over 10 (~10% of the 98-question test). Raw counts stay visible in
// the click-through detail either way. One rule set for BOTH dashboards
// (2026-09-16); `behind` (median-relative pace) was dropped with it.
// The flags a student earned in the test, finished or not (2026-10-06: the
// Finished table keeps them). The live sheets use flagsOf, which drops them
// once the student is done.
function earnedFlagsOf(s) {
  const b = s.behavior;
  if (!b) return [];
  const flags = [];
  if (copyCount(b) >= 4) flags.push('copying');
  if ((b.rapidAnswers || 0) > 10) flags.push('clicking');
  if ((b.tabSwitches || 0) >= 25) flags.push('tabbing');
  return flags;
}
function flagsOf(s) {
  return s.status === 'Done' ? [] : earnedFlagsOf(s);
}

// Everything the monitor counts, in one line (Finished table, 2026-10-06):
// the three flagged behaviors always, right-clicks and dev tools when any.
// Null when the lambda sent no counts (an older backend).
export function behaviorLine(b) {
  if (!b) return null;
  const n = (v) => v || 0;
  return [
    `${n(b.tabSwitches)} tab-away${n(b.tabSwitches) === 1 ? '' : 's'}`,
    `${copyCount(b)} copy/paste`,
    `${n(b.rapidAnswers)} rushed answer${n(b.rapidAnswers) === 1 ? '' : 's'}`,
    ...(b.rightClicks ? [`${b.rightClicks} right-click${b.rightClicks === 1 ? '' : 's'}`] : []),
    ...(b.devTools ? [`${b.devTools} dev-tools attempt${b.devTools === 1 ? '' : 's'}`] : []),
  ].join(' · ');
}

const flagWhy = (key, s, medianAnswered) => {
  const b = s.behavior || {};
  switch (key) {
    case 'copying': return `${copyCount(b)} copy/paste event${copyCount(b) === 1 ? '' : 's'} during the test.`;
    case 'clicking': return `${b.rapidAnswers || 0} question${(b.rapidAnswers || 0) === 1 ? '' : 's'} answered in under 5 seconds.`;
    case 'tabbing': return `Left the test tab ${b.tabSwitches || 0} time${(b.tabSwitches || 0) === 1 ? '' : 's'}.`;
    default: return '';
  }
};

const worstSeverity = (flags) => (flags.some((f) => FLAG_META[f]?.severity === 'red') ? 'red' : 'orange');

/* ---------- small shared UI bits ---------- */

// "See results" (admin, 2026-10-07). A plain link when the page has no router
// (the monitor's own tests render it bare).
function ResultsLink({ to }) {
  const routed = useInRouterContext();
  const className = 'inline-flex items-center gap-1.5 h-[30px] px-3 rounded-[8px] border border-neutral-300 bg-white text-[12px] font-bold text-ink no-underline hover:bg-neutral-50 transition-colors';
  const body = <><BarChart3 size={13} aria-hidden="true" /> See results</>;
  return routed ? <Link to={to} className={className}>{body}</Link> : <a href={to} className={className}>{body}</a>;
}

function Initials({ name, size = 26 }) {
  const parts = (name || '?').trim().split(/\s+/);
  const init = ((parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() || '?';
  return (
    <span
      className="flex-none rounded-full bg-neutral-100 text-neutral-600 font-extrabold flex items-center justify-center"
      style={{ width: size, height: size, fontSize: size * 0.38 }}
    >
      {init}
    </span>
  );
}

function Modal({ title, titleNote, sub, onClose, children, width = 480 }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onMouseDown={onClose}>
      <div
        className="bg-white border border-neutral-200 rounded-[14px] shadow-xl w-full max-h-[85vh] flex flex-col"
        style={{ maxWidth: width }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 pt-4 pb-3 border-b border-neutral-100">
          <div className="min-w-0 text-left">
            <div className="text-[16px] font-extrabold text-ink leading-tight">{title}</div>
            {titleNote && <div className="mt-0.5 text-[12.5px] font-medium text-neutral-500 break-all">{titleNote}</div>}
            {sub && <div className="mt-0.5 text-[12.5px] font-semibold text-neutral-500">{sub}</div>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex-none ml-3 w-7 h-7 rounded-[8px] border-0 bg-transparent text-neutral-400 hover:text-ink hover:bg-neutral-100 cursor-pointer flex items-center justify-center"
          >
            <X size={16} />
          </button>
        </div>
        <div className="overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

// The flagged dot: orange/red, reasoning ONLY on hover (Sandy). Fixed-position
// card so the sheets' overflow containers can't clip it.
function FlagDot({ flags, s, medianAnswered }) {
  const [tip, setTip] = useState(null);
  const ref = useRef(null);
  if (!flags.length) return null;
  const color = worstSeverity(flags) === 'red' ? RED : ORANGE;
  const show = () => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setTip({ x: Math.min(r.left, window.innerWidth - 320), y: r.bottom + 8 });
  };
  return (
    <>
      <span
        ref={ref}
        onMouseEnter={show}
        onMouseLeave={() => setTip(null)}
        className="w-2.5 h-2.5 rounded-full flex-none cursor-default"
        style={{ background: color }}
      />
      {tip && (
        <div
          className="z-50 w-[300px] bg-ink text-white rounded-[10px] px-3.5 py-3 shadow-xl pointer-events-none"
          style={{ position: 'fixed', left: tip.x, top: tip.y }}
        >
          {flags.map((f) => (
            <div key={f} className="flex items-start gap-2 py-0.5">
              <span
                className="flex-none w-2 h-2 rounded-full mt-[5px]"
                style={{ background: FLAG_META[f]?.severity === 'red' ? RED : ORANGE }}
              />
              <p className="m-0 text-[12px] leading-snug">
                <span className="font-extrabold">{FLAG_META[f]?.label}:</span>{' '}
                <span className="text-neutral-300">{flagWhy(f, s, medianAnswered)}</span>
              </p>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

const GHOST_BTN = 'px-2 py-1 text-[11px] font-bold rounded-[6px] border border-neutral-200 bg-white text-neutral-600 hover:text-ink hover:border-neutral-300 cursor-pointer whitespace-nowrap';


// Which diagnostic a page should open on (Sandy 2026-09-16): the one with
// most students mid-test right now; on a tie, the earlier one (Diagnostic 1
// first). When nobody is live it opens on `fallback`: the default below. `scope` (optional Set of ids) narrows a school-wide answer to a class.
export const liveCountOf = (data, scope = null) =>
  (data?.students || []).filter((s) => s.timerStatus === 'active' && (!scope || scope.has(s.studentId))).length;
export const pickLiveDiagnostic = (counts, fallback = DIAGNOSTIC_IDS[0]) => {
  const best = DIAGNOSTIC_IDS.reduce((top, id) => ((counts[id] || 0) > (counts[top] || 0) ? id : top), DIAGNOSTIC_IDS[0]);
  return (counts[best] || 0) > 0 ? best : fallback;
};
// The tab every staff page opens on when nobody is live (Sandy 2026-10-14):
// Diagnostic 3, for every school, even one whose students only see
// Diagnostic 1 and 2 (live students still pull the page to their test).
export const defaultDiagnosticFor = () => 'diagnostic-4';

const Spin = () => <Loader2 size={12} strokeWidth={2.5} className="animate-spin flex-none" role="img" aria-label="Working" />;
const keyOf = (action, studentId) => `${action}:${studentId || ''}`;

/* ---------- the monitor ---------- */

export default function DiagnosticMonitor({
  auth,
  api = requestDiagnostic,
  schoolName,
  scope = 'school', // 'school' (admin) | 'class' (teacher)
  showSettings = false,
  title = 'Diagnostics',
  assignmentId,
  setAssignmentId,
  feed, // { live, loadedOnce, loadError, fetchedAt, refreshing, refresh(force) }
  onDetailChange, // optional: the page mirrors the open student (admin conversation label)
  onRowsChange, // optional: the page reads the computed rows + flags (teacher AI context)
  switchHint, // optional: { count, label, onSwitch, onDismiss } when more of the scope is live on the other diagnostic
  // optional (teacher page only, 2026-09-28): (student, reason, onPausing) => Promise.
  // Resolves once the reset is done; calls onPausing while a live student is
  // being paused first. When absent (admin page) there is no Reset button.
  onReset,
  // optional (2026-10-07): the student detail shows the email under the name.
  // A row's own `email` wins; the admin page passes `emailOf` (studentId =>
  // email) from the school directory, and `emailsLoading` while it loads.
  emailOf,
  emailsLoading = false,
  // optional (admin page, 2026-10-07): assignmentId => link to that
  // diagnostic's results. The button shows once anyone has started it.
  resultsHref,
}) {
  const { live, loadedOnce, loadError, fetchedAt, refreshing, refresh } = feed;
  const emptyClass = !!live?.emptyClass;
  const [busyKey, setBusyKey] = useState(null);
  const busy = !!busyKey;
  const [toast, setToast] = useState(null); // { kind: 'ok' | 'err', text }
  const [detailFor, setDetailFor] = useState(null);
  const [moveFor, setMoveFor] = useState(null);
  const [timeFor, setTimeFor] = useState(null);
  const [resetFor, setResetFor] = useState(null);
  const [resetStage, setResetStage] = useState(null); // null | 'working' | 'pausing'
  const [resetError, setResetError] = useState(null);
  const [confirmPause, setConfirmPause] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showNotStarted, setShowNotStarted] = useState(false);
  // Collapsed sheets by name: 'attention' | 'notFinished' | 'inProgress' | 'finished'.
  const [collapsed, setCollapsed] = useState({});
  const toggle = (name) => () => setCollapsed((c) => ({ ...c, [name]: !c[name] }));
  const limit = scope === 'school' ? SHEET_LIMIT : null;
  const testLabel = diagnosticLabel(assignmentId);

  useEffect(() => { if (onDetailChange) onDetailChange(detailFor); }, [detailFor, onDetailChange]);
  useEffect(() => { setDetailFor(null); setMoveFor(null); setTimeFor(null); setResetFor(null); setResetError(null); setSettingsOpen(false); setConfirmPause(false); }, [assignmentId, schoolName]);

  const toastTimer = useRef(null);
  const say = (kind, text) => {
    setToast({ kind, text });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 6000);
  };
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const onRefresh = () => { if (!refreshing) refresh(true); };

  // Every control action: call, reload, report. schoolName rides on all of
  // them (class pause state and accommodations are school-scoped). `key`
  // names the button that shows the spinner.
  const scoped = (body) => (schoolName ? { ...body, schoolName } : body);
  const act = async (body, okText, key = keyOf(body.action, body.studentId)) => {
    setBusyKey(key);
    try {
      await api(auth, scoped(body));
      // 'live' (2026-09-25): a control changes live state only, so the admin
      // feed re-reads the monitor without the roster and every saved-score
      // page (~30 s at a 2,052-student school). The teacher feed ignores it.
      await refresh('live');
      say('ok', okText);
      return true;
    } catch (e) {
      say('err', e.message || 'Action failed');
      return false;
    } finally {
      setBusyKey(null);
    }
  };
  // Rows are memoized; they get one stable function that always runs the
  // current `act`, so a toast or a modal does not re-render every row.
  const actRef = useRef(act);
  actRef.current = act;
  const rowAct = useCallback((...args) => actRef.current(...args), []);
  // Reset (teacher only): one press. The page's onReset keeps asking while a
  // live student is paused first; the modal stays up with a "Pausing" state
  // until it resolves. A failure shows inside the modal (it stays open), not
  // as a toast that would sit over its buttons on a phone.
  const runReset = async (s, reason) => {
    setBusyKey(keyOf('reset', s.studentId));
    setResetStage('working');
    setResetError(null);
    try {
      await onReset(s, reason, () => setResetStage('pausing'));
      setResetFor(null);
      setDetailFor(null);
      await refresh(true);
      say('ok', `${s.name}'s ${testLabel} was reset. They can retake it.`);
    } catch (e) {
      setResetError(e.message || 'Reset failed. Try again.');
    } finally {
      setBusyKey(null);
      setResetStage(null);
    }
  };
  const limited = !!live?.limited;
  const allRows = useMemo(
    () => [...(live?.students || [])].sort((a, b) => (lastNameKey(a.name) < lastNameKey(b.name) ? -1 : 1)),
    [live]
  );

  // Grade switcher (Sandy 2026-08-17): All grades + each grade the roster
  // actually has. Grade data only exists on new-stack rosters; the control
  // hides itself when the lambda sends no grades. Every sheet, flag, and
  // count below works on the filtered set.
  const [gradeFilter, setGradeFilter] = useState('all');
  const gradesAvailable = useMemo(
    () => [...new Set(allRows.map((s) => s.grade).filter(Boolean))].sort((a, b) => Number(a) - Number(b)),
    [allRows]
  );
  const gradeRows = useMemo(
    () => (gradeFilter === 'all' ? allRows : allRows.filter((s) => s.grade === gradeFilter)),
    [allRows, gradeFilter]
  );
  // Name search (Sandy 2026-09-16): narrows every sheet; the participation
  // bar keeps counting the whole grade/scope so the search never changes
  // "x of y finished".
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const rows = useMemo(
    () => (needle ? gradeRows.filter((s) => (s.name || '').toLowerCase().includes(needle)) : gradeRows),
    [gradeRows, needle]
  );

  const startedRows = useMemo(
    () => rows.filter((s) => (s.started || s.timerStatus) && s.status !== 'Done' && s.answeredTotal != null),
    [rows]
  );
  const medianAnswered = useMemo(() => {
    if (!startedRows.length) return 0;
    const sorted = startedRows.map((s) => s.answeredTotal).sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)];
  }, [startedRows]);

  // Flags depend only on each student's own record, so they are worked out
  // once per poll for the whole list: typing in Search keeps every row's
  // flags array the same and the memoized rows skip re-rendering.
  const flagged = useMemo(() => {
    const map = {};
    allRows.forEach((s) => { map[s.studentId] = flagsOf(s); });
    return map;
  }, [allRows]);

  // Top sheet: flagged students, reds before oranges. Lower sheet: everyone
  // else. A teacher's class view lists students working on the test first,
  // then not started (BBO-61, 2026-10-13: the ones taking it are who the
  // teacher is watching). The school view pages 100 rows at a time and folds a
  // big not-started group, so it keeps not started on top.
  const topRows = useMemo(() => {
    const list = rows.filter((s) => (flagged[s.studentId] || []).length > 0);
    const rank = (s) => Math.min(...(flagged[s.studentId] || []).map((f) => FLAG_ORDER.indexOf(f)).filter((i) => i >= 0));
    return list.sort((a, b) => rank(a) - rank(b) || (lastNameKey(a.name) < lastNameKey(b.name) ? -1 : 1));
  }, [rows, flagged]);

  const lowerRows = useMemo(() => {
    const list = rows.filter((s) => s.status !== 'Done' && (flagged[s.studentId] || []).length === 0);
    const workingFirst = scope === 'class';
    const tier = (s) => {
      if (!s.started && !s.timerStatus) return workingFirst ? 1 : 0;
      if (s.status === 'Done') return 2;
      return workingFirst ? 0 : 1;
    };
    return list.sort((a, b) => tier(a) - tier(b) || (lastNameKey(a.name) < lastNameKey(b.name) ? -1 : 1));
  }, [rows, flagged, scope]);

  const doneRows = useMemo(() => rows.filter((s) => s.status === 'Done'), [rows]);
  const activeCount = useMemo(() => rows.filter((s) => s.timerStatus === 'active').length, [rows]);
  const { allDone, allNotStarted, allEnglishOnly } = useMemo(() => ({
    allDone: gradeRows.filter((s) => s.status === 'Done').length,
    allNotStarted: gradeRows.filter((s) => !s.started && !s.timerStatus && s.status !== 'Done').length,
    allEnglishOnly: gradeRows.filter((s) => englishOnlyScore(s) != null).length,
  }), [gradeRows]);
  const classPaused = live?.classStatus === 'paused';

  // A school of 2,000 before the test is mostly "not started" (2026-09-25):
  // on the admin view a group over 100 folds into a count with "Show list".
  // A search or grade filter that brings it to 100 or fewer shows the rows.
  const notStartedRows = useMemo(() => lowerRows.filter(notStartedOf), [lowerRows]);
  const foldNotStarted = !!limit && notStartedRows.length > limit;
  const inProgressRows = useMemo(() => (foldNotStarted ? lowerRows.filter((s) => !notStartedOf(s)) : lowerRows), [foldNotStarted, lowerRows]);
  // "Show 100 more" starts over when the list itself changes.
  const sheetKey = limit ? `${assignmentId}|${gradeFilter}|${needle}` : undefined;

  useEffect(() => { if (onRowsChange) onRowsChange({ rows, flagged, assignmentId }); }, [rows, flagged, assignmentId, onRowsChange]);

  // Pause-all / unpause-all are the lambda's SCHOOL-wide class_pause /
  // class_unpause: admin (school scope) only. A teacher pauses students one
  // at a time (Sandy 2026-09-16). Unpause-all always shows while the school
  // is paused so a stray pause can't get stuck; pause-all only while a
  // sitting is actually running.
  const pauseAll = () => act({ action: 'class_pause', assignmentId }, 'Everyone paused. Active students drop out within ~40s.', 'class_pause');
  const unpauseAll = () => act({ action: 'class_unpause', assignmentId }, 'Everyone unpaused.', 'class_unpause');
  const showUnpauseAll = scope === 'school' && classPaused;
  const showPauseAll = scope === 'school' && !classPaused && activeCount > 0;
  const scopeNoun = scope === 'school' ? 'school' : 'class';

  const sheetProps = { flagged, medianAnswered, busy, busyKey, act: rowAct, assignmentId, setDetailFor, setMoveFor, setTimeFor, limit };

  return (
    <>
      <div className="diagnostic-header border-b border-neutral-200 px-6 pt-3 pb-3">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="m-0 text-[23px] font-semibold tracking-tight text-ink leading-none">{title}</h1>
              {activeCount > 0 && (
                <span className="inline-flex items-center gap-1.5 text-[11px] font-extrabold text-success-600 uppercase tracking-wide">
                  {/* green, same as the per-student "timer running" dot (Sandy 2026-09-16) */}
                  <span className="w-2 h-2 rounded-full bg-success-500 animate-pulse" /> Live
                </span>
              )}
              {classPaused && (
                <span className="text-[11px] font-extrabold text-error-600 uppercase tracking-wide">School paused</span>
              )}
            </div>
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <div className="inline-flex rounded-[8px] border border-neutral-300 overflow-hidden">
                {ALL_DIAGNOSTICS.map((d, i) => (
                  <button
                    key={d.id}
                    type="button"
                    disabled={busy}
                    onClick={() => setAssignmentId(d.id)}
                    className={
                      'px-3 py-1.5 text-[12px] font-bold cursor-pointer border-0 transition-colors ' +
                      (i > 0 ? 'border-l border-neutral-300 ' : '') +
                      (assignmentId === d.id ? 'bg-ink text-white' : 'bg-white text-neutral-600 hover:bg-neutral-50')
                    }
                  >
                    {d.label}
                  </button>
                ))}
              </div>
              {resultsHref && loadedOnce && live && rows.some((s) => s.status === 'Done' || s.started) && (
                <ResultsLink to={resultsHref(assignmentId)} />
              )}
              {gradesAvailable.length > 0 && (
                <div className="inline-flex rounded-[8px] border border-neutral-300 overflow-hidden">
                  {['all', ...gradesAvailable].map((g, i) => (
                    <button
                      key={g}
                      type="button"
                      onClick={() => setGradeFilter(g)}
                      className={
                        'px-3 py-1.5 text-[12px] font-bold cursor-pointer border-0 transition-colors ' +
                        (i > 0 ? 'border-l border-neutral-300 ' : '') +
                        (gradeFilter === g ? 'bg-ink text-white' : 'bg-white text-neutral-600 hover:bg-neutral-50')
                      }
                    >
                      {g === 'all' ? 'All grades' : `${g}th`}
                    </button>
                  ))}
                </div>
              )}
              <label className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-[8px] border border-neutral-300 bg-white text-neutral-500 focus-within:border-neutral-500">
                <Search size={13} className="flex-none" aria-hidden="true" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search name"
                  aria-label="Search students by name"
                  className="w-[150px] border-0 bg-transparent p-0 text-[12px] font-semibold text-ink placeholder:text-neutral-400 outline-none"
                />
              </label>
              {fetchedAt != null && (
                <button
                  type="button"
                  onClick={onRefresh}
                  title="Refresh now"
                  className="text-[11px] font-semibold text-neutral-500 hover:text-ink tabular-nums bg-transparent border-0 p-0 cursor-pointer transition-colors"
                >
                  {refreshing ? 'Updating…' : 'Refresh'}
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 pb-0.5">
            {showUnpauseAll && (
              <button
                type="button"
                disabled={busy}
                aria-busy={busyKey === 'class_unpause'}
                onClick={unpauseAll}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] border-0 bg-success-600 hover:bg-success-700 text-white text-[12.5px] font-extrabold cursor-pointer disabled:opacity-50"
              >
                {busyKey === 'class_unpause' ? <Spin /> : <Play size={14} />} Unpause all
              </button>
            )}
            {showPauseAll && (
              <button
                type="button"
                disabled={busy}
                onClick={() => setConfirmPause(true)}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] border border-error-200 bg-white hover:bg-error-50 text-error-600 text-[12.5px] font-extrabold cursor-pointer disabled:opacity-50"
              >
                <Pause size={14} /> Pause all
              </button>
            )}
            {showSettings && (
              <button
                type="button"
                onClick={() => setSettingsOpen(true)}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] border border-neutral-300 bg-white hover:bg-neutral-50 text-neutral-700 text-[12.5px] font-extrabold cursor-pointer"
              >
                <Settings size={14} /> Test settings
              </button>
            )}
          </div>
        </div>
        {loadedOnce && live && !emptyClass && <div className="diagnostic-participation"><div className="diagnostic-participation-label"><span><strong>{formatCount(allDone)}</strong> of {formatCount(gradeRows.length)} finished{allEnglishOnly > 0 && ` · ${formatCount(allEnglishOnly)} finished English`}</span><span>{formatCount(gradeRows.length - allDone - allNotStarted)} in progress · {formatCount(allNotStarted)} not started</span></div><div className="diagnostic-progress-bar" role="img" aria-label={`${allDone} of ${gradeRows.length} finished`}><span style={{ width: `${gradeRows.length ? allDone / gradeRows.length * 100 : 0}%` }} /><span style={{ width: `${gradeRows.length ? (gradeRows.length - allDone - allNotStarted) / gradeRows.length * 100 : 0}%` }} /></div></div>}
      </div>

      {!loadedOnce ? (
        <section className="px-6 py-16 text-center text-[13px] text-neutral-500">Loading the live view…</section>
      ) : loadError && !live ? (
        <section className="px-6 pb-16">
          <div className={CARD + ' max-w-[520px] px-8 py-10 text-center mx-auto mt-8'}>
            <div className="text-[16px] font-extrabold text-ink">Couldn't load the diagnostic view</div>
            <p className="mt-2 mb-0 text-[13px] text-neutral-500">{loadError}</p>
            <button type="button" onClick={onRefresh} disabled={refreshing} className={GHOST_BTN + ' mt-4'}>Try again</button>
          </div>
        </section>
      ) : emptyClass ? (
        <section className="px-6 pb-16">
          <div className={CARD + ' max-w-[520px] px-8 py-10 text-center mx-auto mt-8'}>
            <div className="text-[16px] font-extrabold text-ink">No students in this class</div>
            <p className="mt-2 mb-0 text-[13px] text-neutral-500">
              Students show up here once they join. To watch a different class, pick it from the class menu at the top right.
            </p>
          </div>
        </section>
      ) : (
        <section className="diagnostic-sheets px-6 pt-3 pb-6">
          {loadError && <p role="status" className="mb-4 text-[12px] text-amber-800">{loadError}</p>}
          {switchHint && (
            <div role="status" className={CARD + ' mb-4 pl-4 pr-2 py-2 flex items-center gap-3 flex-wrap'}>
              <span className="w-2 h-2 rounded-full bg-success-500 animate-pulse flex-none" aria-hidden="true" />
              <p className="m-0 flex-1 min-w-[180px] text-[13px] text-ink">
                <span className="font-extrabold">{switchHint.count} {switchHint.count === 1 ? 'student' : 'students'}</span>
                {` in this ${scopeNoun} ${switchHint.count === 1 ? 'is' : 'are'} taking ${switchHint.label} right now.`}
              </p>
              <span className="flex items-center gap-1 flex-none">
                <button
                  type="button"
                  disabled={busy}
                  onClick={switchHint.onSwitch}
                  className="px-3 py-1.5 rounded-[8px] border-0 bg-ink text-white text-[12px] font-bold cursor-pointer disabled:opacity-50"
                >
                  Switch to {switchHint.label}
                </button>
                <button
                  type="button"
                  onClick={switchHint.onDismiss}
                  aria-label="Hide this note"
                  className="w-7 h-7 rounded-[8px] border-0 bg-transparent text-neutral-400 hover:text-ink hover:bg-neutral-100 cursor-pointer flex items-center justify-center"
                >
                  <X size={14} />
                </button>
              </span>
            </div>
          )}
          {limited && (
            <div className="mb-5 border border-warning-300 bg-warning-50 text-warning-800 rounded-[10px] px-4 py-3 text-[12.5px] leading-relaxed">
              <strong>Basic view.</strong> Live sections and timers are real; per-question progress and
              behavior flags switch on once the updated <code className="text-[11.5px]">adminDiagnosticControls</code> lambda is deployed.
            </div>
          )}

          {/* ===== TOP SHEET: flagged ===== */}
          {topRows.length > 0 && <div data-tour="diag-top" className="mb-4">
            <SectionToggle open={!collapsed.attention} onToggle={toggle('attention')} title="Needs attention" count={topRows.length} className="mb-2" />
            <div hidden={!!collapsed.attention}>
              <SheetTable key={sheetKey} rows={topRows} {...sheetProps} />
            </div>
          </div>}

          {/* ===== LOWER SHEET: everyone else ===== */}
          <div data-tour="diag-table">
            <SectionToggle open={!collapsed.notFinished} onToggle={toggle('notFinished')} title="Not finished" count={lowerRows.length} className="mb-2" />
            <div hidden={!!collapsed.notFinished}>
            {lowerRows.length === 0 ? (
              <div className={CARD + ' px-4 py-6 text-center text-[13px] text-neutral-500'}>
                {rows.length === 0
                  ? (needle ? `No one named "${query.trim()}" in this ${scopeNoun}.` : `No students found for this ${scopeNoun}.`)
                  : doneRows.length === rows.length ? 'Everyone has finished.' : 'Remaining students are in Needs attention.'}
              </div>
            ) : foldNotStarted ? (
              <>
                <div className={CARD + ' pl-4 pr-2 py-2 flex items-center gap-3 flex-wrap'}>
                  <p className="m-0 flex-1 min-w-[180px] text-[13px] text-neutral-600">
                    <span className="font-extrabold text-ink tabular-nums">{formatCount(notStartedRows.length)}</span> students have not started this test.
                  </p>
                  <button
                    type="button"
                    aria-expanded={showNotStarted}
                    onClick={() => setShowNotStarted((v) => !v)}
                    className={GHOST_BTN + ' px-3 py-1.5 text-[12px]'}
                  >
                    {showNotStarted ? 'Hide list' : 'Show list'}
                  </button>
                </div>
                {showNotStarted && <div className="mt-2"><SheetTable key={sheetKey} rows={notStartedRows} {...sheetProps} /></div>}
                {inProgressRows.length > 0 && (
                  <>
                    <SectionToggle small open={!collapsed.inProgress} onToggle={toggle('inProgress')} title="In progress" count={inProgressRows.length} className="mt-4 mb-2" />
                    <div hidden={!!collapsed.inProgress}>
                      <SheetTable key={sheetKey} rows={inProgressRows} {...sheetProps} />
                    </div>
                  </>
                )}
              </>
            ) : (
              <SheetTable key={sheetKey} rows={lowerRows} {...sheetProps} />
            )}
            </div>
          </div>
          {doneRows.length > 0 && (
            <section data-tour="diag-done" className="mt-4">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <h2 className="m-0 flex text-[15px]"><SectionToggle open={!collapsed.finished} onToggle={toggle('finished')} title="Finished" count={doneRows.length} /></h2>
                {live?.scoresPending && <span role="status" className="text-[12px] text-neutral-500">Loading saved scores…</span>}
              </div>
              <div hidden={!!collapsed.finished}>
                <FinishedTable key={sheetKey} rows={doneRows} onOpen={setDetailFor} limit={limit} />
              </div>
            </section>
          )}
        </section>
      )}

      {/* ===== modals ===== */}
      {detailFor && (
        <DetailModal
          s={rows.find((r) => r.studentId === detailFor.studentId) || detailFor}
          flags={flagged[detailFor.studentId] || []}
          medianAnswered={medianAnswered}
          email={detailFor.email || emailOf?.(detailFor.studentId) || (emailsLoading ? 'Loading email…' : null)}
          limited={limited}
          busy={busy}
          busyKey={busyKey}
          act={act}
          assignmentId={assignmentId}
          onMove={() => setMoveFor(detailFor)}
          onTime={() => setTimeFor(detailFor)}
          onReset={onReset ? () => setResetFor(detailFor) : null}
          onClose={() => setDetailFor(null)}
        />
      )}
      {resetFor && (
        <ResetModal
          s={rows.find((r) => r.studentId === resetFor.studentId) || resetFor}
          testLabel={testLabel}
          stage={resetStage}
          error={resetError}
          onClose={() => { setResetFor(null); setResetError(null); }}
          onConfirm={(reason) => runReset(resetFor, reason)}
        />
      )}
      {moveFor && (
        <MoveModal
          s={moveFor}
          busy={busy}
          working={busyKey === keyOf('move_section', moveFor.studentId)}
          onClose={() => setMoveFor(null)}
          onMove={async (target) => {
            const okd = await act(
              { action: 'move_section', assignmentId, studentId: moveFor.studentId, targetSection: target },
              `${moveFor.name} moved. If they're mid-test they re-enter with the change in effect.`
            );
            if (okd) setMoveFor(null);
          }}
        />
      )}
      {timeFor && (
        <TimeModal
          s={timeFor}
          busy={busy}
          working={busyKey === keyOf('set_time', timeFor.studentId)}
          onClose={() => setTimeFor(null)}
          onSet={async (minutes) => {
            const okd = await act(
              { action: 'set_time', assignmentId, studentId: timeFor.studentId, newTimeRemaining: minutes * 60 },
              `${timeFor.name}'s section time set to ${minutes} min.`
            );
            if (okd) setTimeFor(null);
          }}
        />
      )}
      {confirmPause && (
        <Modal title="Pause the whole school?" onClose={() => setConfirmPause(false)} width={440}>
          <div className="px-5 py-4">
            <p className="m-0 text-[13px] text-neutral-600 leading-relaxed">
              Every active student gets kicked out of the test within ~40 seconds, and nobody can enter
              until you unpause. Timers stop while paused.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setConfirmPause(false)} className={GHOST_BTN + ' px-3.5 py-2 text-[12.5px]'}>
                Cancel
              </button>
              <button
                type="button"
                disabled={busy}
                aria-busy={busyKey === 'class_pause'}
                onClick={async () => { const okd = await pauseAll(); if (okd) setConfirmPause(false); }}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[6px] border-0 bg-error-600 hover:bg-error-700 text-white text-[12.5px] font-extrabold cursor-pointer disabled:opacity-50"
              >
                {busyKey === 'class_pause' && <Spin />} {busyKey === 'class_pause' ? 'Pausing…' : 'Pause all'}
              </button>
            </div>
          </div>
        </Modal>
      )}
      {showSettings && settingsOpen && <SettingsModal auth={auth} api={api} schoolName={schoolName} say={say} onClose={() => setSettingsOpen(false)} />}

      {/* toast: bottom-centre, not bottom-right, so the Buffy launcher
          (.admin-buffy, fixed right/bottom) never covers it (Sandy 2026-09-16) */}
      {toast && (
        <div
          className={
            'fixed bottom-24 sm:bottom-6 left-1/2 -translate-x-1/2 z-[70] max-w-[min(420px,calc(100vw-32px))] w-max px-4 py-3 rounded-[10px] shadow-xl text-[12.5px] font-semibold leading-snug text-center ' +
            (toast.kind === 'ok' ? 'bg-ink text-white' : 'bg-error-600 text-white')
          }
        >
          {toast.text}
        </div>
      )}
    </>
  );
}

/* ---------- sheets ---------- */

// Module is 160px (was 130) so "Break · R&W 330" fits (2026-09-23).
const SHEET_COLS = 'minmax(210px,1.6fr) 160px 90px 80px 210px';

function Th({ children, left }) {
  return (
    <div className={'px-4 py-2.5 text-[11px] font-extrabold tracking-[0.04em] uppercase text-neutral-500 flex items-center' + (left ? '' : ' justify-end text-right')}>
      {children}
    </div>
  );
}

// The spreadsheet: no Why column, the orange/red dot by the name carries the
// flags (hover for reasons), and an Actions column with the proctor controls.
// `limit` (admin only) shows that many rows, then "Show N more".
function SheetTable({ rows, flagged, medianAnswered, busy, busyKey, act, assignmentId, setDetailFor, setMoveFor, setTimeFor, limit = null }) {
  const [shown, setShown] = useState(limit);
  const visible = limit ? rows.slice(0, shown) : rows;
  return (
    <>
    <div className="diagnostic-live-wrap">
      <div className={CARD + ' diagnostic-live-table overflow-hidden'} style={{ minWidth: 760 }}>
        <div className="diagnostic-live-head grid items-stretch bg-neutral-50 border-b border-neutral-200" style={{ gridTemplateColumns: SHEET_COLS }}>
          <Th left>Student</Th>
          <Th>Module</Th>
          <Th>Question</Th>
          <Th>Time</Th>
          <Th>Actions</Th>
        </div>
        <div className="divide-y divide-neutral-100">
          {visible.map((s) => (
            <SheetRow
              key={s.studentId}
              s={s}
              flags={flagged[s.studentId] || NO_FLAGS}
              medianAnswered={medianAnswered}
              busy={busy}
              pausing={busyKey === keyOf('individual_pause', s.studentId)}
              unpausing={busyKey === keyOf('individual_unpause', s.studentId)}
              act={act}
              assignmentId={assignmentId}
              setDetailFor={setDetailFor}
              setMoveFor={setMoveFor}
              setTimeFor={setTimeFor}
            />
          ))}
        </div>
      </div>
    </div>
    {limit && rows.length > visible.length && (
      <MoreRows shown={visible.length} total={rows.length} step={limit} onMore={() => setShown((n) => n + limit)} />
    )}
    </>
  );
}

const NO_FLAGS = [];

// One sheet row. Memoized (2026-09-25): it re-renders only when its student,
// flags or busy state change, not on every toast, modal or keystroke.
const SheetRow = memo(function SheetRow({ s, flags, medianAnswered, busy, pausing, unpausing, act, assignmentId, setDetailFor, setMoveFor, setTimeFor }) {
  const q = questionCell(s);
  const notStarted = !s.started && !s.timerStatus && s.status !== 'Done';
  const done = s.status === 'Done';
  const adminPaused = adminPausedOf(s);
  const canControl = !done && !!s.timerStatus;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => setDetailFor(s)}
      onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setDetailFor(s); } }}
      title={`See ${s.name}'s detail`}
      className="diagnostic-live-row grid items-center w-full min-h-[44px] text-left bg-white hover:bg-neutral-50 cursor-pointer"
      style={{ gridTemplateColumns: SHEET_COLS }}
    >
      <div className="flex items-center gap-2.5 px-4 py-1.5 min-w-0">
        <span
          className={
            'w-2 h-2 rounded-full flex-none ' +
            (s.timerStatus === 'active' ? 'bg-success-500' : s.timerStatus === 'paused' ? 'bg-neutral-300' : 'bg-transparent')
          }
        />
        <Initials name={s.name} />
        <span title={s.name} className="text-[13.5px] font-bold text-ink truncate">{s.name}</span>
        <FlagDot flags={flags} s={s} medianAnswered={medianAnswered} />
        {adminPaused && <span className="text-[10.5px] font-extrabold text-error-600 uppercase tracking-wide flex-none">{adminPausePending(s) ? 'Pausing' : 'Paused by you'}</span>}
      </div>
      <div className={'px-4 text-[12.5px] font-semibold text-right whitespace-nowrap ' + (notStarted || done ? 'text-neutral-400' : 'text-neutral-700')}>
        {moduleCell(s)}
        {/* no-break spaces: on phones it wraps as "Break ·" / "R&W 330" */}
        {englishOnlyScore(s) != null && <span className="text-neutral-500">{' · '}R&amp;W{' '}{englishOnlyScore(s)}</span>}
      </div>
      <div className="px-4 text-[12.5px] font-bold text-neutral-700 tabular-nums text-right whitespace-nowrap">
        {q ? <>Q {q.furthest || '–'}<span className="text-neutral-400 font-semibold">/{q.total}</span></> : <span className="text-neutral-300">—</span>}
      </div>
      <div className="px-4 text-[12.5px] font-semibold text-neutral-700 tabular-nums text-right whitespace-nowrap">
        {!done && s.timeRemaining != null ? formatTime(Math.max(0, s.timeRemaining)) : <span className="text-neutral-300">—</span>}
      </div>
      <div className="px-4 py-1.5 flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
        {canControl ? (
          <>
            {adminPaused ? (
              <button
                type="button"
                disabled={busy}
                aria-busy={unpausing}
                onClick={() => act({ action: 'individual_unpause', assignmentId, studentId: s.studentId }, `${s.name} unpaused.`)}
                className={GHOST_BTN + ' inline-flex items-center gap-1 text-success-700 border-success-200 hover:bg-success-50'}
              >
                {unpausing && <Spin />} Unpause
              </button>
            ) : s.timerStatus === 'active' ? (
              <button
                type="button"
                disabled={busy}
                aria-busy={pausing}
                onClick={() => act({ action: 'individual_pause', assignmentId, studentId: s.studentId }, `${s.name} pauses within ~40s.`)}
                className={GHOST_BTN + ' inline-flex items-center gap-1 text-error-600 border-error-200 hover:bg-error-50'}
              >
                {pausing && <Spin />} Pause
              </button>
            ) : null}
            <button type="button" disabled={busy} onClick={() => setMoveFor(s)} className={GHOST_BTN}>Move</button>
            <button type="button" disabled={busy} onClick={() => setTimeFor(s)} className={GHOST_BTN}>Time</button>
          </>
        ) : (
          <span className="text-[11.5px] font-semibold text-neutral-300">{done ? 'finished' : 'not started'}</span>
        )}
      </div>
    </div>
  );
});

/* ---------- modals ---------- */

// Anything of this diagnostic on record: an answer sheet, a timer, a finished
// test, or a saved score (English-only, or left behind by a reset that only
// partly finished, which shows as "not started"). Nothing there = nothing to reset.
// Exported for Buffy's chat (2026-10-06): the reset dialog asks "which diagnostic?" only
// when more than one test has an attempt.
export const hasAttempt = (s) => s.status === 'Done' || !!s.started || !!s.timerStatus
  || finishedScore(s, 'math') != null || finishedScore(s, 'reading') != null;

function DetailModal({ s, flags, medianAnswered, email, limited, busy, busyKey, act, assignmentId, onMove, onTime, onReset, onClose }) {
  const b = s.behavior || {};
  const behaviorBits = [
    `${b.tabSwitches || 0} tab-aways`,
    `${copyCount(b)} copy/paste`,
    `${b.rapidAnswers || 0} rushed answers`,
    ...(b.devTools ? [`${b.devTools} dev-tools attempts`] : []),
  ];
  const adminPaused = adminPausedOf(s);
  const canControl = s.status !== 'Done' && !!s.timerStatus;
  const pausing = busyKey === keyOf('individual_pause', s.studentId);
  const unpausing = busyKey === keyOf('individual_unpause', s.studentId);
  const showReset = !!onReset && hasAttempt(s);
  return (
    <Modal title={s.name} titleNote={email} sub={s.status === 'Done' ? 'Finished' : sectionLabel(s.currentSection)} onClose={onClose} width={520}>
      <div className="px-5 py-4">
        {s.status === 'Done' && <div className="diagnostic-detail-scores">{['total', 'math', 'reading'].map(key => <div key={key}><span>{key === 'reading' ? 'English' : key === 'math' ? 'Math' : 'Total'}</span><strong>{finishedScore(s, key) ?? '—'}</strong></div>)}</div>}
        {flags.length > 0 && (
          <div className="mb-4 flex flex-col gap-1.5">
            {flags.map((f) => (
              <div key={f} className="flex items-start gap-2.5">
                <span
                  className="flex-none w-2 h-2 rounded-full mt-1.5"
                  style={{ background: FLAG_META[f]?.severity === 'red' ? RED : ORANGE }}
                />
                <p className="m-0 text-[13px] text-neutral-700 leading-relaxed flex-1 min-w-0">
                  <span className="font-bold text-ink">{FLAG_META[f]?.label || f}:</span> {flagWhy(f, s, medianAnswered)}
                </p>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-4 text-[12.5px] text-neutral-600 mb-4 flex-wrap">
          {s.timeRemaining != null && s.status !== 'Done' && (
            <span><span className="font-bold text-ink tabular-nums">{formatTime(Math.max(0, s.timeRemaining))}</span> left in section</span>
          )}
          {s.lastHeartbeat && <span>last update <Ago iso={s.lastHeartbeat} /></span>}
          {adminPaused && <span className="font-bold text-error-600">{adminPausePending(s) ? "Pausing, applies on the student's next heartbeat" : 'Paused by an admin'}</span>}
        </div>

        {!limited && (
          <>
            <div className="border border-neutral-200 rounded-[10px] overflow-hidden mb-3">
              {MODULES.map((mod, i) => {
                const stat = moduleStatOf(s, mod);
                const pct = stat && stat.total ? Math.min(100, Math.round((stat.answered / stat.total) * 100)) : 0;
                return (
                  <div
                    key={mod.key}
                    className={'diagnostic-module-row flex items-center gap-3 px-3.5 py-2.5 bg-white' + (i < MODULES.length - 1 ? ' border-b border-neutral-100' : '')}
                  >
                    <span className="w-[130px] flex-none text-[12.5px] font-bold text-ink">{mod.title.replace(' · ', ' ')}</span>
                    <span className="flex-1 h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                      <span className="block h-full rounded-full bg-primary-500" style={{ width: `${pct}%` }} />
                    </span>
                    <span className="flex-none text-[12px] font-semibold text-neutral-600 tabular-nums w-[110px] text-right">
                      {stat && (stat.answered > 0 || stat.furthest > 0)
                        ? <>{stat.answered}/{stat.total} · on Q {stat.furthest || '–'}</>
                        : <span className="text-neutral-300">not reached</span>}
                    </span>
                  </div>
                );
              })}
            </div>
            <p className="m-0 text-[12px] text-neutral-500">{behaviorBits.join(' · ')}</p>
          </>
        )}

        {canControl && (
          <div className="mt-4 pt-4 border-t border-neutral-100 flex items-center gap-2 flex-wrap">
            {adminPaused ? (
              <button
                type="button"
                disabled={busy}
                aria-busy={unpausing}
                onClick={() => act({ action: 'individual_unpause', assignmentId, studentId: s.studentId }, `${s.name} unpaused.`)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[7px] border-0 bg-success-600 hover:bg-success-700 text-white text-[12px] font-extrabold cursor-pointer disabled:opacity-50"
              >
                {unpausing ? <Spin /> : <Play size={13} />} Unpause
              </button>
            ) : s.timerStatus === 'active' ? (
              <button
                type="button"
                disabled={busy}
                aria-busy={pausing}
                onClick={() => act({ action: 'individual_pause', assignmentId, studentId: s.studentId }, `${s.name} pauses within ~40s.`)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[7px] border border-error-200 bg-white hover:bg-error-50 text-error-600 text-[12px] font-extrabold cursor-pointer disabled:opacity-50"
              >
                {pausing ? <Spin /> : <Pause size={13} />} Pause
              </button>
            ) : null}
            <button type="button" disabled={busy} onClick={onMove} className={GHOST_BTN + ' px-3 py-1.5 text-[12px]'}>Move section</button>
            <button type="button" disabled={busy} onClick={onTime} className={GHOST_BTN + ' px-3 py-1.5 text-[12px]'}>Set time</button>
          </div>
        )}
        {/* Teacher reset (2026-09-28): its own row, apart from the proctor
            controls, so a destructive action never sits in the pause row. */}
        {showReset && (
          <div className="mt-4 pt-4 border-t border-neutral-100 flex items-center justify-between gap-3 flex-wrap">
            <p className="m-0 flex-1 min-w-[180px] text-left text-[12px] text-neutral-500 leading-snug">Clear this test so they can retake it.</p>
            <button
              type="button"
              disabled={busy}
              onClick={onReset}
              className="flex-none inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[7px] border border-error-200 bg-white hover:bg-error-50 text-error-600 text-[12px] font-extrabold cursor-pointer disabled:opacity-50"
            >
              <RotateCcw size={13} /> Reset test
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}

// Confirm + progress for the teacher's one-press reset (2026-09-28). Stays
// open while it runs: 'working' = the request is out, 'pausing' = the student
// was in the test and is being paused first (up to a minute).
// Exported for Buffy's chat (2026-10-06): the chat opens this same dialog.
export function ResetModal({ s, testLabel, stage, error, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  const running = !!stage;
  const inTest = s.timerStatus === 'active' && !adminPausedOf(s);
  const first = (s.name || '').trim().split(/\s+/)[0] || 'the student';
  return (
    <Modal title={`Reset ${testLabel}?`} sub={s.name} onClose={running ? () => {} : onClose} width={460}>
      <div className="px-5 py-4 text-left">
        <ul className="m-0 pl-4 list-disc marker:text-neutral-300 flex flex-col gap-1.5 text-[13px] text-neutral-600 leading-relaxed text-pretty">
          <li>Their answers, scores, timer and tab/copy flags for {testLabel} are cleared. A copy is kept in the reset history.</li>
          <li>They take {testLabel} again from the beginning.</li>
          <li>When they finish it, their levels in every area are set again from the new scores. Finished homework keeps its grades. Homework in progress restarts at the new level.</li>
          {inTest && <li className="font-semibold text-ink">They are in the test right now, so they get paused first. That takes up to a minute.</li>}
        </ul>
        <label htmlFor="diagnostic-reset-reason" className="block mt-4 mb-1.5 text-[12px] font-bold text-neutral-600">Reason (optional)</label>
        <textarea
          id="diagnostic-reset-reason"
          value={reason}
          maxLength={500}
          rows={2}
          disabled={running}
          onChange={(e) => setReason(e.target.value)}
          placeholder="For example: rushed through it, or copied answers"
          className="block w-full rounded-[8px] border border-neutral-300 bg-white px-3 py-2 text-[13px] text-ink placeholder:text-neutral-400 outline-none focus:border-neutral-500 resize-y disabled:opacity-60"
        />
        {stage === 'pausing' && (
          <p role="status" className="mt-3 mb-0 text-[12.5px] font-semibold text-neutral-600 text-pretty">
            Pausing {first}. Keep this page open until the reset finishes (this may take up to a minute).
          </p>
        )}
        {error && !running && (
          <p role="alert" className="mt-3 mb-0 text-[12.5px] font-semibold text-error-600 text-pretty">{error}</p>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" disabled={running} onClick={onClose} className={GHOST_BTN + ' px-3.5 py-2 text-[12.5px] disabled:opacity-50'}>
            Cancel
          </button>
          <button
            type="button"
            disabled={running}
            aria-busy={running}
            onClick={() => onConfirm(reason.trim())}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[6px] border-0 bg-error-600 hover:bg-error-700 text-white text-[12.5px] font-extrabold cursor-pointer disabled:opacity-60"
          >
            {running && <Spin />} {stage === 'pausing' ? `Pausing ${first}…` : running ? 'Resetting…' : `Reset ${testLabel}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function MoveModal({ s, busy, working, onClose, onMove }) {
  const [target, setTarget] = useState(`${SECTIONS[0].field}-${SECTIONS[0].number}`);
  return (
    <Modal title="Move student" sub={`${s.name} · now in ${sectionLabel(s.currentSection)}`} onClose={onClose} width={440}>
      <div className="px-5 py-4">
        <label className="block text-[12px] font-bold text-neutral-600 mb-1.5">Move to</label>
        <CleanSelect aria-label="Move to"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
        >
          {SECTIONS.map((sec) => (
            <option key={`${sec.field}-${sec.number}`} value={`${sec.field}-${sec.number}`}>{sec.label}</option>
          ))}
        </CleanSelect>
        <p className="mt-3 mb-0 text-[12px] text-neutral-500 leading-relaxed">
          They get kicked out and can re-enter immediately in the new section. Existing answers are kept.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className={GHOST_BTN + ' px-3.5 py-2 text-[12.5px]'}>Cancel</button>
          <button
            type="button"
            disabled={busy}
            aria-busy={working}
            onClick={() => {
              const [field, number] = target.split('-');
              onMove({ field, number });
            }}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[6px] border-0 bg-ink hover:bg-neutral-800 text-white text-[12.5px] font-extrabold cursor-pointer disabled:opacity-50"
          >
            {working && <Spin />} {working ? 'Moving…' : 'Move'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// Exported for Buffy's chat (2026-10-06), like ResetModal.
export function TimeModal({ s, busy, working, onClose, onSet }) {
  const [minutes, setMinutes] = useState(
    s.timeRemaining != null ? Math.max(0, Math.round(s.timeRemaining / 60)) : 32
  );
  return (
    <Modal title="Set section time" sub={`${s.name} · ${formatTime(s.timeRemaining)} left now`} onClose={onClose} width={420}>
      <div className="px-5 py-4">
        <label className="block text-[12px] font-bold text-neutral-600 mb-1.5">New time (minutes)</label>
        <input
          type="number"
          min={0}
          max={120}
          value={minutes}
          onChange={(e) => setMinutes(Math.max(0, Math.min(120, Number(e.target.value))))}
        />
        <p className="mt-3 mb-0 text-[12px] text-neutral-500 leading-relaxed">
          = {formatTime(minutes * 60)}. They get kicked out and can re-enter immediately with the new time.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className={GHOST_BTN + ' px-3.5 py-2 text-[12.5px]'}>Cancel</button>
          <button
            type="button"
            disabled={busy}
            aria-busy={working}
            onClick={() => onSet(minutes)}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[6px] border-0 bg-ink hover:bg-neutral-800 text-white text-[12.5px] font-extrabold cursor-pointer disabled:opacity-50"
          >
            {working && <Spin />} {working ? 'Setting…' : 'Set time'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// Accommodations CSV + submission mode, the "affects all diagnostics"
// settings from the legacy controls, same lambda actions and validation.
// Admin (school scope) only.
function SettingsModal({ auth, api, schoolName, say, onClose }) {
  const [submissionMode, setSubmissionMode] = useState(null);
  const [modeBusy, setModeBusy] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvResult, setCsvResult] = useState(null);

  useEffect(() => {
    let alive = true;
    api(auth, { action: 'get_submission_mode', schoolName })
      .then((d) => { if (alive) setSubmissionMode(d.submissionMode || 'manual_only'); })
      .catch(() => { if (alive) setSubmissionMode('manual_only'); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeMode = async (mode) => {
    setModeBusy(true);
    try {
      await api(auth, { action: 'set_submission_mode', submissionMode: mode, schoolName });
      setSubmissionMode(mode);
      say('ok', 'Submission mode updated.');
    } catch (e) {
      say('err', e.message);
    } finally {
      setModeBusy(false);
    }
  };

  const upload = async () => {
    if (!csvFile) return;
    if (!csvFile.name.endsWith('.csv')) { setCsvResult({ error: 'Only .csv files are accepted' }); return; }
    setCsvBusy(true);
    setCsvResult(null);
    try {
      const text = await csvFile.text();
      const lines = text.split('\n').map((l) => l.trim()).filter((l) => l);
      const header = lines[0].toLowerCase().split(',').map((h) => h.trim());
      if (header.length !== 2 || !header.includes('email') || !header.includes('multiplier')) {
        setCsvResult({ error: 'CSV must have exactly two columns: email and multiplier' });
        return;
      }
      const emailIdx = header.indexOf('email');
      const multIdx = header.indexOf('multiplier');
      const accommodations = lines.slice(1).map((line) => {
        const c = line.split(',').map((x) => x.trim());
        return { email: c[emailIdx], multiplier: Number(c[multIdx]) };
      }).filter((a) => a.email && a.multiplier);
      if (!accommodations.length) { setCsvResult({ error: 'No valid rows found in the CSV' }); return; }
      const bad = accommodations.filter((a) => ![1.5, 2].includes(a.multiplier));
      if (bad.length) {
        setCsvResult({ error: `Multiplier must be 1.5 or 2. Fix: ${bad.map((a) => a.email).join(', ')}` });
        return;
      }
      const data = await api(auth, { action: 'upload_accommodations', accommodations, columns: header, schoolName });
      setCsvResult(data);
      setCsvFile(null);
    } catch (e) {
      setCsvResult({ error: e.message });
    } finally {
      setCsvBusy(false);
    }
  };

  const exportNotFound = (emails) => {
    const blob = new Blob(['email\n' + emails.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'skipped_emails.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Modal title="Test settings" sub="These apply to all diagnostics for your school" onClose={onClose} width={560}>
      <div className="px-5 py-4 flex flex-col gap-6">
        {/* accommodations */}
        <div>
          <div className="text-[13.5px] font-extrabold text-ink mb-1">Time accommodations</div>
          <p className="mt-0 mb-3 text-[12.5px] text-neutral-500 leading-relaxed">
            Upload a CSV with two columns — <code className="text-[11.5px] bg-neutral-100 px-1 rounded">email</code> and{' '}
            <code className="text-[11.5px] bg-neutral-100 px-1 rounded">multiplier</code> (1.5 or 2). Listed students get
            that multiple of the standard time on every section.
          </p>
          <div className="flex items-center gap-2.5 flex-wrap">
            <input
              type="file"
              accept=".csv"
              onChange={(e) => {
                const f = e.target.files[0];
                if (f && !f.name.endsWith('.csv')) { setCsvResult({ error: 'Only .csv files are accepted' }); e.target.value = ''; return; }
                setCsvFile(f || null);
                setCsvResult(null);
              }}
              className="text-[12.5px] min-w-0 max-w-full"
            />
            <button
              type="button"
              disabled={!csvFile || csvBusy}
              onClick={upload}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] border-0 bg-ink hover:bg-neutral-800 text-white text-[12.5px] font-extrabold cursor-pointer disabled:opacity-40"
            >
              <Upload size={13} /> {csvBusy ? 'Uploading…' : 'Upload'}
            </button>
          </div>
          {csvResult && (
            <div className="mt-3 flex flex-col gap-2">
              {csvResult.error ? (
                <div className="px-3.5 py-2.5 rounded-[8px] bg-error-50 border border-error-200 text-error-700 text-[12.5px]">{csvResult.error}</div>
              ) : (
                <>
                  {csvResult.message && (
                    <div className="px-3.5 py-2.5 rounded-[8px] bg-success-50 border border-success-200 text-success-700 text-[12.5px] font-semibold">
                      {csvResult.message}
                    </div>
                  )}
                  {csvResult.notFound?.length > 0 && (
                    <div className="px-3.5 py-2.5 rounded-[8px] bg-warning-50 border border-warning-300 text-warning-800 text-[12.5px]">
                      <div className="font-bold mb-1">{csvResult.notFound.length} email{csvResult.notFound.length === 1 ? ' was' : 's were'} not found and skipped:</div>
                      <div className="max-h-[90px] overflow-y-auto leading-relaxed">{csvResult.notFound.join(', ')}</div>
                      <button
                        type="button"
                        onClick={() => exportNotFound(csvResult.notFound)}
                        className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-[6px] border border-warning-300 bg-white text-warning-800 text-[11.5px] font-bold cursor-pointer"
                      >
                        <Download size={12} /> Export list
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* submission mode */}
        <div>
          <div className="text-[13.5px] font-extrabold text-ink mb-1">Submission mode</div>
          <p className="mt-0 mb-3 text-[12.5px] text-neutral-500 leading-relaxed">
            Takes effect from each student's next section — set it before the test starts.
          </p>
          <div className="flex flex-col gap-2">
            {[
              { value: 'auto_only', label: 'Auto only', desc: 'Students wait the full time; sections auto-submit at the end (like the real SAT).' },
              { value: 'manual_and_auto', label: 'Manual & auto', desc: 'Students can submit early; sections still auto-submit at the end.' },
              { value: 'manual_only', label: 'Manual only', desc: 'No timer enforcement — students submit whenever.' },
            ].map((m) => (
              <button
                key={m.value}
                type="button"
                disabled={modeBusy || submissionMode === null}
                onClick={() => changeMode(m.value)}
                className={
                  'text-left px-3.5 py-2.5 rounded-[10px] border-2 cursor-pointer transition-colors disabled:opacity-60 bg-white ' +
                  (submissionMode === m.value ? 'border-primary-500 bg-primary-50/40' : 'border-neutral-200 hover:border-neutral-300')
                }
              >
                <div className="text-[13px] font-extrabold text-ink">{m.label}{submissionMode === m.value && <span className="ml-2 text-[11px] font-extrabold text-primary-600 uppercase">Current</span>}</div>
                <div className="text-[12px] text-neutral-500 mt-0.5">{m.desc}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}

export function finishedScore(student, key) {
  const number = value => ['number', 'string'].includes(typeof value) && value !== '' && Number.isInteger(Number(value)) && Number(value) >= 200 && Number(value) <= 800 ? Number(value) : null;
  const math = number(student.score?.math);
  const english = number(student.score?.reading);
  return key === 'math' ? math : key === 'reading' ? english : math != null && english != null ? math + english : null;
}
// A finished student's name cell (2026-10-06): the flag dot they earned in
// the test on its left (same colors and hover reasons as the live sheets),
// and every count the monitor keeps under the name. Every row keeps the dot's
// slot, so the names line up.
function FinishedName({ student, onOpen }) {
  const line = behaviorLine(student.behavior);
  return (
    <div className="diagnostic-finished-name">
      <span className="diagnostic-finished-dot"><FlagDot flags={earnedFlagsOf(student)} s={student} /></span>
      <span className="diagnostic-finished-who">
        <button onClick={() => onOpen(student)}>{student.name}</button>
        {line && <small>{line}</small>}
      </span>
    </div>
  );
}

// `limit` (admin only, 2026-09-25): sort the whole list, show that many rows,
// then "Show N more". The teacher view passes none and shows everyone.
// Flagged students stay on top (2026-10-07): reds, then oranges, then
// everyone else; the column sort orders each group.
const attentionRank = (s) => {
  const flags = earnedFlagsOf(s);
  return !flags.length ? 2 : worstSeverity(flags) === 'red' ? 0 : 1;
};
export function FinishedTable({ rows, onOpen, limit = null }) {
  const [sort, setSort] = useState({ key: 'name', direction: 1 });
  const [shown, setShown] = useState(limit);
  const sorted = useMemo(() => [...rows].sort((a, b) => {
    const group = attentionRank(a) - attentionRank(b);
    if (group) return group;
    if (sort.key === 'name') return a.name.localeCompare(b.name) * sort.direction;
    const av = finishedScore(a, sort.key), bv = finishedScore(b, sort.key);
    if (av == null) return bv == null ? 0 : 1;
    if (bv == null) return -1;
    return (av - bv) * sort.direction;
  }), [rows, sort]);
  const visible = limit ? sorted.slice(0, shown) : sorted;
  return <><div className="diagnostic-finished-wrap"><table className="diagnostic-finished"><thead><tr>{[['name', 'Student'], ['total', 'Total'], ['math', 'Math'], ['reading', 'English']].map(([key, label]) => <th key={key} aria-sort={sort.key === key ? sort.direction === 1 ? 'ascending' : 'descending' : 'none'}><button onClick={() => setSort({ key, direction: sort.key === key ? -sort.direction : key === 'name' ? 1 : -1 })}>{label} <span aria-hidden="true">{sort.key === key ? sort.direction === 1 ? '↑' : '↓' : '↕'}</span></button></th>)}</tr></thead><tbody>{visible.map(student => <tr key={student.studentId}><td><FinishedName student={student} onOpen={onOpen} /></td>{['total', 'math', 'reading'].map(key => <td key={key}>{finishedScore(student, key) ?? <span title="Score not available yet">—</span>}</td>)}</tr>)}</tbody></table></div>{limit && sorted.length > visible.length && <MoreRows shown={visible.length} total={sorted.length} step={limit} onMore={() => setShown((n) => n + limit)} />}</>;
}


// earnedFlagsOf / FLAG_META / worstSeverity / flagWhy: the admin diagnostic
// results page lists flagged students with the same rule set (2026-10-07).
export { sectionLabel, flagsOf, Modal, earnedFlagsOf, FLAG_META, FLAG_ORDER, worstSeverity, flagWhy };
